import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  NotificationService,
  TpReportGeneratorService,
  EntitiesService, UtilsService,
  SessionService,
  CompSearchService
} from '../../../shared/services';
import {
  InternalLocalFileReportInstance,
  EntityValidationErrorResponse,
  SelectableTransaction,
  Entity,
  Transaction,
  GenerateReportPayload,
  FinancialDataValueEnteredInEnum,
  Container,
  LocalFileReportVersionModalPayload,
  CurrencyType,
} from '../../../shared/models';
import { PaginationConstants, TranslationConstants, LanguageConstants, TinyTextEditor } from '../../../shared/constants';
import {
  ClosableModalWithErrorResponseModel
} from 'src/app/shared/components/base/closable-modal-with-error-response-model';
import { ModalService } from 'src/app/shared/modules/modal';
import { Subscription } from 'rxjs';

@Component({
  selector: 'report-version-modal',
  templateUrl: './report-version-modal.component.html',
})
export class ReportVersionModalComponent extends ClosableModalWithErrorResponseModel<EntityValidationErrorResponse>
  implements OnInit, OnDestroy {

  data: LocalFileReportVersionModalPayload;
  internalLocalFileInstances: InternalLocalFileReportInstance[] = [];
  responseErrors: any[] = [];
  inputCurrencyOptions: CurrencyType[] = [];

  pageSizes: number[];

  fileToUpload: File;
  subscriptions: Subscription[] = [];
  reportTitle: string;
  tradingPartnerId: number;
  potentialTradingPartners: Entity[] = [];
  transactions: SelectableTransaction[] = [];
  viewedTransactions: SelectableTransaction[] = [];
  selectedStudyId: number = null;
  currencyOptionId: number;
  templateId: string;
  currentPage = 1;
  totalItems: number;
  selectedPageSize = PaginationConstants.DEFAULT_PAGE_SIZE;
  reportStage: string;

  language: string;
  languages = [];

  generatingReport = false;
  generatedReport = false;
  workingContainer: Container;

  executiveSummaryIntro: string = null;
  executiveSummaryConclusion: string = null;

  tinyInit = TinyTextEditor.INIT;

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _tpReportGeneratorService: TpReportGeneratorService,
    private _entitiesService: EntitiesService,
    private _sessionService: SessionService,
    private _utilsService: UtilsService,
    private _compSearchService: CompSearchService
  ) {
    super(notificationService, modalService);
    this.validationErrors = new EntityValidationErrorResponse();
    this.workingContainer = this._sessionService.getWorkingContainer();
  }

  ngOnInit() {
    this.setFormDefaultValues();
    this.listenForErrorMessages();
    this.getCurrencyOptions();
    this.getLanguages(this.data.entity.taxJurisdiction.countryId, this.workingContainer.taxYear);
  }

  setFormDefaultValues(): void {
    this.executiveSummaryConclusion = this.data.latestGeneratedVersion.executiveSummaryConclusion;
    this.executiveSummaryIntro = this.data.latestGeneratedVersion.executiveSummaryIntro;
    this.pageSizes = PaginationConstants.PAGE_SIZES;
    this.potentialTradingPartners = this.data.potentialTradingPartners;
    this.language = LanguageConstants.BY_NAME.ENGLISH.ISO_CODE;
    // this is a hack, couldn't get the dropdown to populate without it
    setTimeout(() => { this.templateId = this.data.templates[0].templateId; });
  }

  getCurrencyOptions(): void {
    if (this.data && this.data.entity &&
      this.data.entity.legalEntityCurrencyConversion &&
      this.data.entity.legalEntityCurrencyConversion.length) {
      this.inputCurrencyOptions = this._utilsService.enumToArray(FinancialDataValueEnteredInEnum);
    } else {
      this.inputCurrencyOptions = [{
        id: 1,
        name: FinancialDataValueEnteredInEnum[1],
      }];
    }
    this.setDefaultCurrency();
  }

  setDefaultCurrency(): void {
    const defaultCurrency: CurrencyType = this.inputCurrencyOptions.find((currency: CurrencyType) => {
      const currencyIso: string = this.getCurrencyIsoCode(currency.id);
      return (currencyIso === this.data.latestGeneratedVersion.reportCurrency.isoCode);
    });
    this.currencyOptionId = defaultCurrency && defaultCurrency.id;
  }

  getCurrencyIsoCode(optionId: number): string {
    let currency = this.data && this.data.taxJurisdictionCurrency && this.data.taxJurisdictionCurrency.isoCode;
    if (optionId === FinancialDataValueEnteredInEnum.MNE) {
      currency = this.data && this.data.mneCurrency && this.data.mneCurrency.isoCode;
    }

    return `${currency}`;
  }

  tradingPartnerChange(tradingPartnerId: number): void {
    this.tradingPartnerId = tradingPartnerId;
    this.selectedStudyId = null;
    this.getNewTransactions();
  }

  getLanguages(jurisdictionId: number, taxYear: number): void {
    this._compSearchService.getLanguages(jurisdictionId, taxYear).subscribe((res) => {
      this.languages = res.data.sort((language1, language2) => {
        return language1.language_iso > language2.language_iso ? 1 : -1;
      }).map((language) => {
        return language.language_iso;
      });
      this.setDefaultLanguage(this.languages);
    });
  }

  private setDefaultLanguage(languages: string[]): void {
    if (languages && languages.length > 0) {
      const lastUsedLanguageIso: string = languages.find(language => language === this.data.latestGeneratedVersion.languageIso);
      const englishLanguageIso: string = languages.find(language => language === LanguageConstants.BY_NAME.ENGLISH.ISO_CODE);

      const languageToUseIso: string = lastUsedLanguageIso || englishLanguageIso || languages[0];

      this.language = languageToUseIso;
    }
  }

  generateInternalLocalfileReport(): void {
    const selectedTransactions = this.transactions.filter(transaction => transaction.selected);
    const selectedTransactionIds = selectedTransactions.map(transaction => transaction.transactionId);
    let currencyId;

    if (this.currencyOptionId === 1) {
      currencyId = this.data.mneCurrency.currencyId;
    } else if (this.currencyOptionId === 2) {
      currencyId = this.data.taxJurisdictionCurrency.currencyId;
    }

    const payload: GenerateReportPayload = {
      selectedTransactionIds,
      currencyId,
      name: this.data.internalLocalFile.name,
      templateId: this.templateId,
      containerId: this.workingContainer.containerId,
      primaryEntityId: this.data.entity.entityId,
      tradingPartnerId: this.tradingPartnerId,
      mneCurrency: this.currencyOptionId === 1,
      mneId: this.data.mneId,
      targetLanguage: this.language,
      newReportInstance: true,
      reportStage: this.reportStage,
      internalLocalfileReportId: this.data.internalLocalFile.internalLocalfileReportId,
      executiveSummaryIntro: this.executiveSummaryIntro,
      executiveSummaryConclusion: this.executiveSummaryConclusion,
      taxYear: this.workingContainer.taxYear
    };

    this.responseErrors = [];
    const errorMessages = this.validateReportPayload(payload);

    if (errorMessages.length) {
      this.responseErrors = errorMessages;
      return;
    }

    this.generatingReport = true;
    this._tpReportGeneratorService.generateReport(payload).subscribe(() => {
      this.generatingReport = false;
      this.generatedReport = true;

      // success is here becuse we need the exitModal function to close for both success and failures
      this.success();
      this.exitModal();
    });
  }

  getNewTransactions(transactionToKeep?: SelectableTransaction) {
    this.currentPage = 1;
    this._entitiesService.getEntityPairTransactions(
      this.data.entity.entityId, this.tradingPartnerId,
      `?${this.getPaginationQueryString()}&${this.getStudyQueryString()}`
    ).subscribe((res) => {
      let transactionToKeepIsInNewTransactions = false;

      this.transactions = res.data.map((transaction: any) => {
        if (transactionToKeep && transactionToKeep.transactionId === transaction.transactionId) {
          transaction.selected = true;
          transactionToKeepIsInNewTransactions = true;
        } else {
          transaction.selected = false;
        }
        return transaction;
      });

      this.viewedTransactions = this.transactions;

      if (transactionToKeep && !transactionToKeepIsInNewTransactions) {
        this.transactions.push(transactionToKeep);
      }

      this.totalItems = res.count;
    });
  }

  getStudyQueryString(): string {
    let queryString = '';

    if (this.selectedStudyId) {
      queryString = `study=${this.selectedStudyId}`;
    }

    return queryString;
  }

  selectedTransactionChange(transactionId: number): void {
    const foundTransaction = this.transactions.find(transaction => transaction.transactionId === transactionId);

    foundTransaction.selected = !foundTransaction.selected;

    if (foundTransaction.selected) {
      if (this.selectedStudyId !== foundTransaction.study.studyId) {
        this.selectedStudyId = foundTransaction.study.studyId;
        this.getNewTransactions(foundTransaction);
      }
    } else {
      const noSelectedTransactions = this.transactions.every(transaction => !transaction.selected);
      if (noSelectedTransactions) {
        this.selectedStudyId = null;
        this.getNewTransactions();
      }
    }
  }

  getPaginationQueryString(): string {
    const queryParameters = [];
    let queryString: string;
    if (!!this.selectedPageSize) {
      queryParameters.push(`length=${this.selectedPageSize}`);
    }
    if (!!this.currentPage) {
      queryParameters.push(`page=${this.currentPage}`);
    }
    if (queryParameters.length > 0) {
      queryString = `${queryParameters.join('&')}`;
    }
    return queryString;
  }

  pageChanged(event: any): void {
    this.currentPage = event.page;
    this.getNextTransactions();
  }

  pageSizeChanged(): void {
    this.getNextTransactions();
  }

  getNextTransactions(): void {
    this._entitiesService.getEntityPairTransactions(
      this.data.entity.entityId,
      this.tradingPartnerId,
      `?${this.getPaginationQueryString()}&${this.getStudyQueryString()}`
    ).subscribe((res) => {
      this.viewedTransactions = res.data.map((transaction: any) => {
        const matchedTransaction = this.transactions.find(trs => trs.transactionId === transaction.transactionId);

        transaction.selected = matchedTransaction ? matchedTransaction.selected : false;

        return transaction;
      });
      this.addNewTransactions(this.viewedTransactions);
      this.totalItems = res.count;
    });
  }

  addNewTransactions(newTransactions: SelectableTransaction[]): void {
    newTransactions.forEach((newTransaction) => {
      if (!this.transactions.find(transaction => transaction.transactionId === newTransaction.transactionId)) {
        this.transactions.push(newTransaction);
      }
    });
  }

  getEntityCode(transaction: Transaction, isSource = true): string {
    return this._utilsService.getEntityCodeFromTransaction(transaction, isSource);
  }

  validateReportPayload(payload: any): string[] {
    const errorMessages = [];

    if (!payload.tradingPartnerId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_TRADING_PARTNER_SELECTED);
    }

    if (!payload.currencyId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_CURRENCY_SELECTED);
    }

    if (!payload.templateId) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_TEMPLATE_MUST_BE_SELECTED);
    }

    if (payload.selectedTransactionIds.length === 0) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_NO_TRANSACTIONS_SELECTED);
    }

    if (payload.reportStage !== 'Draft' && payload.reportStage !== 'Final') {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_STAGE_MUST_BE_SELECTED);
    }

    if (payload.executiveSummaryIntro === '' || payload.executiveSummaryIntro === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_EXECUTIVE_SUMMARY_INTRO_MISSING);
    }

    if (payload.executiveSummaryConclusion === '' || payload.executiveSummaryConclusion === null) {
      errorMessages.push(TranslationConstants.REPORT_GENERATION_ERROR_EXECUTIVE_SUMMARY_CONCLUSION_MISSING);
    }

    return errorMessages;
  }

  exitModal(): void {
    this.closeAllModals();
  }

}
